import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpOpen, setMobileExpOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [desktopLangOpen, setDesktopLangOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);
  const desktopLangRef = useRef(null);
  const mobileLangRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileExpOpen(false);
    setDropdownOpen(false);
    setDesktopLangOpen(false);
    setMobileLangOpen(false);
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Click outside to close language dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopLangRef.current && !desktopLangRef.current.contains(event.target)) {
        setDesktopLangOpen(false);
      }
      if (mobileLangRef.current && !mobileLangRef.current.contains(event.target)) {
        setMobileLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const languages = [
    { code: 'da', label: 'Dansk', short: 'DA', flag: '🇩🇰' },
    { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
    { code: 'de', label: 'Deutsch', short: 'DE', flag: '🇩🇪' },
    { code: 'no', label: 'Norsk', short: 'NO', flag: '🇳🇴' },
    { code: 'sv', label: 'Svenska', short: 'SV', flag: '🇸🇪' },
  ];

  const currentLangObj = languages.find((l) => l.code === lang) || languages[0];

  const experienceLabels = {
    hestetur: { da: 'Hestetur (25 €)', en: 'Horseback Riding (25 €)', de: 'Reitausflug (25 €)', no: 'Ridetur (25 €)', sv: 'Ridtur (25 €)' },
    baadture: { da: 'Bådture til øer (fra 27 €)', en: 'Island Boat Trips (from 27 €)', de: 'Insel-Bootstouren (ab 27 €)', no: 'Båtturer til øyer (fra 27 €)', sv: 'Båtturer till öar (från 27 €)' },
    speedboat: { da: 'Speedboat White Island (150 €)', en: 'Speedboat White Island (150 €)', de: 'Speedboot White Island (150 €)', no: 'Speedbåt White Island (150 €)', sv: 'Speedbåt White Island (150 €)' },
    safari: { da: 'Safari tur (30 €)', en: 'Safari Tour (30 €)', de: 'Wüstensafari (30 €)', no: 'Safartur (30 €)', sv: 'Safartur (30 €)' },
    supersafari: { da: 'Super safari tur (30 €)', en: 'Super Safari Tour (30 €)', de: 'Super Wüstensafari (30 €)', no: 'Super safaritur (30 €)', sv: 'Super safaritur (30 €)' },
    storbaad: { da: 'Stor bådtur (op til 20 pers.)', en: 'Large Boat (up to 20 pax)', de: 'Große Bootstour (bis 20 Pers.)', no: 'Stor båttur (opptil 20 pers.)', sv: 'Stor båttur (upp till 20 pers.)' },
    citytour: { da: 'Citytour Hurghada (20 €)', en: 'Hurghada City Tour (20 €)', de: 'Stadtrundfahrt Hurghada (20 €)', no: 'Byvandring Hurghada (20 €)', sv: 'Stadstur Hurghada (20 €)' },
    private: { da: 'Private VIP ture', en: 'Private VIP Tours', de: 'Private VIP Touren', no: 'Private VIP turer', sv: 'Privata VIP turer' },
  };

  const experienceLinks = [
    { path: '/oplevelser/hestetur', label: experienceLabels.hestetur[lang] || experienceLabels.hestetur.da, icon: 'fa-solid fa-horse' },
    { path: '/oplevelser/baadture', label: experienceLabels.baadture[lang] || experienceLabels.baadture.da, icon: 'fa-solid fa-ship' },
    { path: '/oplevelser/speedboat', label: experienceLabels.speedboat[lang] || experienceLabels.speedboat.da, icon: 'fa-solid fa-bolt' },
    { path: '/oplevelser/safari-tur', label: experienceLabels.safari[lang] || experienceLabels.safari.da, icon: 'fa-solid fa-sun-plant-wilt' },
    { path: '/oplevelser/supersafari-hurghada', label: experienceLabels.supersafari[lang] || experienceLabels.supersafari.da, icon: 'fa-solid fa-car-side' },
    { path: '/oplevelser/stor-baadtur', label: experienceLabels.storbaad[lang] || experienceLabels.storbaad.da, icon: 'fa-solid fa-users-rays' },
    { path: '/oplevelser/citytour-hurghada', label: experienceLabels.citytour[lang] || experienceLabels.citytour.da, icon: 'fa-solid fa-city' },
    { path: '/oplevelser/private-ture', label: experienceLabels.private[lang] || experienceLabels.private.da, icon: 'fa-solid fa-crown' },
  ];

  const getNavLinkClass = (isActive) => {
    if (scrolled) {
      return isActive
        ? "text-amber-600 font-extrabold transition-all px-3 py-1.5 rounded-full bg-amber-50"
        : "text-slate-700 hover:text-slate-950 font-semibold transition-colors px-3 py-1.5 rounded-full hover:bg-slate-100/80";
    }
    return isActive
      ? "text-white font-extrabold transition-all px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm shadow-sm"
      : "text-white/90 hover:text-white font-semibold transition-colors px-3 py-1.5 rounded-full hover:bg-white/10";
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md text-slate-900 shadow-sm border-b border-slate-200/80 py-1.5 sm:py-2' 
          : 'bg-gradient-to-b from-slate-950/85 via-slate-950/40 to-transparent text-white py-2 sm:py-2.5 border-b border-white/5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 group -my-3 sm:-my-4 md:-my-5 py-0.5">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=90,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/screenshot-2026-08-06-at-14-45-09-removebg-preview.png" 
              alt="Captain Hurghada Tours" 
              className="h-16 sm:h-20 md:h-24 w-auto object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation Links (Center) */}
          <nav className="hidden lg:flex items-center gap-1.5 xl:gap-2 text-sm">
            <NavLink to="/" end className={({ isActive }) => getNavLinkClass(isActive)}>
              {t('nav.home')}
            </NavLink>

            <NavLink to="/om-os" className={({ isActive }) => getNavLinkClass(isActive)}>
              {t('nav.about')}
            </NavLink>

            {/* Oplevelser Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <NavLink 
                to="/oplevelser" 
                className={({ isActive }) => 
                  location.pathname.startsWith('/oplevelser') 
                    ? `${getNavLinkClass(true)} flex items-center gap-1.5` 
                    : `${getNavLinkClass(false)} flex items-center gap-1.5`
                }
              >
                <span>{t('nav.experiences')}</span>
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                  dropdownOpen ? 'rotate-180' : ''
                }`} aria-hidden="true" />
              </NavLink>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2.5 w-72 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="rounded-2xl bg-white border border-slate-200/80 shadow-xl p-2 text-slate-900">
                    <Link
                      to="/oplevelser"
                      className="block px-3.5 py-2 rounded-xl text-xs font-bold text-slate-500 hover:text-amber-600 hover:bg-amber-50 uppercase tracking-wider transition-all mb-1"
                    >
                      {lang === 'da' ? 'Se alle oplevelser →' : lang === 'de' ? 'Alle Erlebnisse ansehen →' : 'View all experiences →'}
                    </Link>
                    <div className="space-y-0.5 max-h-[70vh] overflow-y-auto">
                      {experienceLinks.map((item) => {
                        const isSubActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                              isSubActive
                                ? 'bg-amber-500 text-white font-bold shadow-sm'
                                : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
                            }`}
                          >
                            <i className={`${item.icon} text-xs w-4 text-center shrink-0 ${isSubActive ? 'text-white' : 'text-amber-500'}`} aria-hidden="true" />
                            <span className="truncate">{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/planlaeg-din-rejse" className={({ isActive }) => getNavLinkClass(isActive)}>
              {t('nav.planTrip')}
            </NavLink>

            <NavLink to="/faq" className={({ isActive }) => getNavLinkClass(isActive)}>
              {t('nav.faq')}
            </NavLink>

            <NavLink to="/kontakt" className={({ isActive }) => getNavLinkClass(isActive)}>
              {t('nav.contact')}
            </NavLink>
          </nav>

          {/* Right Action Items */}
          <div className="hidden lg:flex items-center gap-3">
            
            {/* Multi-Language Dropdown Switcher */}
            <div className="relative" ref={desktopLangRef}>
              <button
                type="button"
                onClick={() => setDesktopLangOpen(!desktopLangOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all cursor-pointer ${
                  scrolled 
                    ? 'border-slate-300 bg-slate-100 text-slate-800 hover:bg-slate-200' 
                    : 'border-white/20 bg-slate-900/60 backdrop-blur-sm text-white hover:bg-white/10'
                }`}
                title="Skift sprog / Switch language"
              >
                <span>{currentLangObj.flag}</span>
                <span className="uppercase">{currentLangObj.short}</span>
                <i className={`fa-solid fa-chevron-down text-[9px] transition-transform duration-200 ${desktopLangOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              {desktopLangOpen && (
                <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-2xl shadow-2xl border border-slate-200/90 py-1.5 z-50 text-slate-900 animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    Vælg sprog / Select language
                  </div>
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setLang(l.code);
                        setDesktopLangOpen(false);
                      }}
                      onClick={() => {
                        setLang(l.code);
                        setDesktopLangOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 text-xs font-semibold transition-colors cursor-pointer text-left ${
                        lang === l.code
                          ? 'bg-amber-50 text-amber-600 font-bold'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="text-base leading-none">{l.flag}</span>
                        <span>{l.label}</span>
                      </span>
                      {lang === l.code && (
                        <i className="fa-solid fa-check text-amber-500 text-xs" aria-hidden="true" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Instagram Quick Link */}
            <a
              href="https://www.instagram.com/captainhurgada/"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all ${
                scrolled 
                  ? 'text-slate-700 hover:text-amber-600 hover:bg-slate-100' 
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
              title="Følg os på Instagram @captainhurgada"
            >
              <i className="fa-brands fa-instagram text-lg" aria-hidden="true" />
            </a>

            {/* CTA Button */}
            <Link
              to="/kontakt"
              className={`px-4 py-2 rounded-full font-bold text-xs tracking-wide transition-all shadow-sm ${
                scrolled
                  ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/20'
                  : 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold shadow-amber-500/30'
              }`}
            >
              {t('hero.ctaContact')}
            </Link>
          </div>

          {/* Mobile Right Controls (< lg) */}
          <div className="flex lg:hidden items-center gap-2">
            
            {/* Mobile Lang Dropdown Trigger */}
            <div className="relative" ref={mobileLangRef}>
              <button
                type="button"
                onClick={() => setMobileLangOpen(!mobileLangOpen)}
                className={`px-2.5 py-1 rounded-full border text-xs font-bold transition-all flex items-center gap-1 cursor-pointer ${
                  scrolled
                    ? 'border-slate-300 bg-slate-100 text-slate-900'
                    : 'border-white/25 bg-slate-900/60 backdrop-blur-sm text-white'
                }`}
              >
                <span>{currentLangObj.flag}</span>
                <span className="uppercase">{currentLangObj.short}</span>
              </button>

              {mobileLangOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-white rounded-2xl shadow-2xl border border-slate-200 py-1.5 z-50 text-slate-900 animate-in fade-in duration-150">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setLang(l.code);
                        setMobileLangOpen(false);
                      }}
                      onClick={() => {
                        setLang(l.code);
                        setMobileLangOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold cursor-pointer ${
                        lang === l.code ? 'bg-amber-50 text-amber-600 font-bold' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span>{l.flag}</span>
                        <span>{l.label}</span>
                      </span>
                      {lang === l.code && <i className="fa-solid fa-check text-amber-500 text-[10px]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/captainhurgada/"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                scrolled ? 'text-slate-800 hover:text-amber-600' : 'text-white hover:text-amber-300'
              }`}
              aria-label="Instagram"
            >
              <i className="fa-brands fa-instagram text-base" aria-hidden="true" />
            </a>

            {/* Hamburger Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                scrolled ? 'text-slate-800 hover:bg-slate-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Åbn menu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Drawer Menu (< lg) */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-slate-950/98 backdrop-blur-xl text-white flex flex-col justify-between p-6 sm:p-8 overflow-y-auto animate-in fade-in duration-200">
          <div>
            {/* Top Bar inside Menu */}
            <div className="flex items-center justify-between pb-5 border-b border-white/10">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=90,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/screenshot-2026-08-06-at-14-45-09-removebg-preview.png" 
                  alt="Captain Hurghada Tours" 
                  className="h-14 sm:h-16 w-auto object-contain drop-shadow-md"
                />
              </Link>

              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                aria-label="Luk menu"
              >
                <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
              </button>
            </div>

            {/* Language Selection Row in Mobile Menu */}
            <div className="py-4 border-b border-white/10">
              <span className="block text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">Vælg sprog / Select language</span>
              <div className="grid grid-cols-5 gap-1.5">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`py-2 px-1 rounded-xl text-center flex flex-col items-center justify-center gap-1 text-xs font-bold transition-all ${
                      lang === l.code
                        ? 'bg-amber-500 text-slate-950 shadow-md font-extrabold scale-105'
                        : 'bg-white/10 text-slate-200 hover:bg-white/15'
                    }`}
                  >
                    <span className="text-base">{l.flag}</span>
                    <span className="text-[10px]">{l.short}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Menu Links */}
            <nav className="py-4 flex flex-col space-y-1 text-base font-semibold">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className={`py-2.5 px-4 rounded-xl transition-all ${
                  location.pathname === '/' ? 'bg-amber-500 text-slate-950 font-bold' : 'text-slate-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.home')}
              </Link>

              <Link 
                to="/om-os" 
                onClick={() => setMobileMenuOpen(false)}
                className={`py-2.5 px-4 rounded-xl transition-all ${
                  location.pathname === '/om-os' ? 'bg-amber-500 text-slate-950 font-bold' : 'text-slate-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.about')}
              </Link>

              {/* Mobile Experiences Accordion */}
              <div>
                <button
                  onClick={() => setMobileExpOpen(!mobileExpOpen)}
                  className="w-full flex items-center justify-between py-2.5 px-4 rounded-xl text-slate-200 hover:bg-white/10 hover:text-white transition-all text-left"
                >
                  <span>{t('nav.experiences')}</span>
                  <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${mobileExpOpen ? 'rotate-180 text-amber-400' : ''}`} aria-hidden="true" />
                </button>

                {mobileExpOpen && (
                  <div className="pl-4 pr-2 py-2 space-y-1 bg-white/5 rounded-2xl mt-1 mb-1 border border-white/10 max-h-56 overflow-y-auto">
                    <Link
                      to="/oplevelser"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 px-3 text-xs font-bold uppercase tracking-wider text-amber-400 hover:text-amber-300"
                    >
                      {lang === 'da' ? 'Se alle oplevelser →' : 'View all experiences →'}
                    </Link>
                    {experienceLinks.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2.5 py-2 px-3 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/10"
                      >
                        <i className={`${item.icon} text-xs text-amber-400 w-4`} aria-hidden="true" />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link 
                to="/planlaeg-din-rejse" 
                onClick={() => setMobileMenuOpen(false)}
                className={`py-2.5 px-4 rounded-xl transition-all ${
                  location.pathname === '/planlaeg-din-rejse' ? 'bg-amber-500 text-slate-950 font-bold' : 'text-slate-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.planTrip')}
              </Link>

              <Link 
                to="/faq" 
                onClick={() => setMobileMenuOpen(false)}
                className={`py-2.5 px-4 rounded-xl transition-all ${
                  location.pathname === '/faq' ? 'bg-amber-500 text-slate-950 font-bold' : 'text-slate-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.faq')}
              </Link>

              <Link 
                to="/kontakt" 
                onClick={() => setMobileMenuOpen(false)}
                className={`py-2.5 px-4 rounded-xl transition-all ${
                  location.pathname === '/kontakt' ? 'bg-amber-500 text-slate-950 font-bold' : 'text-slate-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.contact')}
              </Link>
            </nav>
          </div>

          {/* Drawer Footer */}
          <div className="pt-6 border-t border-white/10 space-y-4">
            <Link 
              to="/kontakt" 
              onClick={() => setMobileMenuOpen(false)}
              className="w-full py-3.5 px-4 rounded-2xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-center block shadow-lg shadow-amber-500/20 text-sm tracking-wide"
            >
              {t('hero.ctaContact')}
            </Link>

            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>Captain Hurghada Tours</span>
              <a
                href="https://www.instagram.com/captainhurgada/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-slate-300 hover:text-amber-400"
              >
                <i className="fa-brands fa-instagram text-sm" aria-hidden="true" />
                <span>@captainhurgada</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
